import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { List } from 'immutable';
import moment from 'moment';
import {
    getThreadChatMessages,
    readThreadChatMessage,
} from 'flow/actions/chat-actions';
import { MessageThreadCheckProvider } from 'flow/contexts/MessageThreadCheckProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useRovingMessageList from 'flow/hooks/useRovingMessageList';
import MessageGroup, {
    GroupedMessageProps,
} from 'flow/pages/messages/MessageGroup';
import { Message } from 'flow/types/Chats';
import { ChatMessageKey, ChatSessionKey } from 'flow/types/EntityKeys';

import { TypedMap } from 'flow/types/TypedMap';

interface ThreadMessageScrollerProps {
    messageKey: ChatMessageKey;
    sessionKey: ChatSessionKey;
    register: (methods: { updateMessageSend: () => void }) => void;
}

const ThreadMessageList = ({
    messageKey,
    sessionKey,
    register,
}: ThreadMessageScrollerProps) => {
    const { i18next } = useAppContext();
    const threadListContainerRef = useRef<HTMLDivElement | null>(null);
    const rovingHandlers = useRovingMessageList(threadListContainerRef);
    const threadEndPlaceHolderRef = useRef<HTMLDivElement | null>(null);
    const lastFetchedThreadMessageKey = useRef<ChatMessageKey | null>(null);
    const isSelfRef = useRef(false);
    const [currentScrollHeight, setCurrentScrollHeight] = useState(0);
    const [currentScrollTop, setCurrentScrollTop] = useState(0);
    const [isBottom, setIsBottom] = useState(true);
    const dispatch = useAppDispatch<any>();
    const { ownExtension } = useExtension();

    const imageLoaded = useAppSelector((state) =>
        state.chats.get('attachmentImageLoading'),
    );

    const threadMessages = useAppSelector((state) =>
        state.chats.getIn([
            'threadMessage',
            'threadMessages',
            sessionKey,
            messageKey,
        ]),
    );

    useEffect(() => {
        register({
            updateMessageSend,
        });
    }, [register]);

    const isAtBottom = (
        containerRef: React.MutableRefObject<HTMLDivElement>,
    ) => {
        if (!containerRef.current) {
            return false;
        }
        const { scrollTop, clientHeight, scrollHeight } = containerRef.current;
        const scrollClientHeight = Math.abs(scrollTop + clientHeight) + 50;
        return scrollClientHeight > scrollHeight;
    };

    const updateMessageSend = () => threadScrollUntilBottom(true);

    const threadScrollUntilBottom = useCallback(
        (isSelf = false) => {
            const threadElement = threadListContainerRef.current;
            isSelfRef.current = isSelf;
            if (threadElement) {
                const { scrollHeight, scrollTop, clientHeight } = threadElement;

                const isCurrentlyBottom =
                    scrollHeight === scrollTop + clientHeight && scrollTop > 0;

                const hasScroll = scrollHeight > clientHeight;

                if (isCurrentlyBottom) {
                    isSelfRef.current = false;
                }

                if (
                    ((!isCurrentlyBottom && isBottom) || isSelfRef.current) &&
                    hasScroll
                ) {
                    threadElement.scrollTop += 100;
                    requestAnimationFrame(() =>
                        threadScrollUntilBottom(isSelf),
                    );
                }
            }
        },
        [threadListContainerRef, isBottom],
    );

    const isOwnLastMessage = useMemo(
        () =>
            ownExtension.get('key') ===
            threadMessages?.last()?.getIn(['from', 'extensionKey']),
        [ownExtension, threadMessages],
    );

    useEffect(() => {
        const lastReadMessage = threadMessages?.last();
        if (lastReadMessage?.get('key') && !lastReadMessage.get('read')) {
            dispatch(
                readThreadChatMessage(
                    sessionKey,
                    messageKey,
                    lastReadMessage.get('key'),
                ),
            );
        }

        if (
            lastReadMessage?.get('key') &&
            lastReadMessage.get('key') !== lastFetchedThreadMessageKey.current
        ) {
            lastFetchedThreadMessageKey.current = lastReadMessage.get('key');
            dispatch(getThreadChatMessages(sessionKey, messageKey, null)).then(
                updateScrollerHeight,
            );
        }
    }, [dispatch, messageKey, sessionKey, threadMessages]);

    useEffect(() => {
        if (isAtBottom(threadListContainerRef) || isOwnLastMessage) {
            threadScrollUntilBottom(false);
        } else if (
            !isAtBottom(threadListContainerRef) &&
            currentScrollHeight <= threadListContainerRef.current?.scrollHeight
        ) {
            threadListContainerRef?.current.scrollTo({
                top:
                    threadListContainerRef.current?.scrollHeight -
                    currentScrollHeight +
                    currentScrollTop,
                behavior: 'instant',
            });
        }
    }, [threadMessages, messageKey, imageLoaded, isOwnLastMessage]);

    const fetchOldThreadMessages = useCallback(
        (beforeKey: ChatMessageKey) => {
            dispatch(
                getThreadChatMessages(sessionKey, messageKey, beforeKey),
            ).then(updateScrollerHeight);
        },
        [dispatch, messageKey, sessionKey],
    );

    const updateScrollerHeight = () => {
        setCurrentScrollHeight(threadListContainerRef.current?.scrollHeight);
        setCurrentScrollTop(threadListContainerRef.current?.scrollTop);
    };

    const groupMessages = (messages: List<TypedMap<Message>>) => {
        const display: GroupedMessageProps[] = [];
        let currentDateGroup: GroupedMessageProps | undefined;

        messages?.forEach((message) => {
            const date = moment(message?.get('sent')).format('YYYY-MM-DD');
            if (currentDateGroup?.date !== date) {
                currentDateGroup = { date, messages: [] };
                display.push(currentDateGroup);
            }

            currentDateGroup.messages.push(message);
        });

        return display;
    };

    const scrollThreadMessages = useCallback(() => {
        const { scrollTop } = threadListContainerRef.current;
        if (scrollTop <= 50) {
            setIsBottom(false);
            fetchOldThreadMessages(threadMessages?.first()?.get('key'));
        }
    }, [fetchOldThreadMessages, threadListContainerRef, threadMessages]);

    useEffect(() => {
        if (threadListContainerRef.current) {
            threadListContainerRef.current.addEventListener(
                'scrollend',
                scrollThreadMessages,
            );
        }
        return () => {
            threadListContainerRef?.current?.removeEventListener(
                'scrollend',
                scrollThreadMessages,
            );
        };
    }, [threadMessages, threadListContainerRef]);

    useEffect(() => {
        setCurrentScrollHeight(0);
    }, [messageKey, sessionKey]);

    useEffect(() => {
        setIsBottom(true);
    }, [threadMessages, messageKey, sessionKey]);

    return (
        <div
            ref={threadListContainerRef}
            role="feed"
            aria-label={i18next.t('ThreadMessages.DefaultTitle')}
            {...rovingHandlers}
            className="scrollbar h-fit flex-1 items-end overflow-x-hidden p-3"
        >
            <MessageThreadCheckProvider value={true}>
                {groupMessages(threadMessages)?.map((group) => (
                    <MessageGroup
                        key={group.date}
                        group={group}
                        sessionKey={sessionKey}
                        showContextMenu={false}
                    />
                ))}
            </MessageThreadCheckProvider>

            <div className="bottom-0 block h-3" ref={threadEndPlaceHolderRef} />
        </div>
    );
};

export default ThreadMessageList;
